import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Plus, Trash2, Search } from 'lucide-react';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

/**
 * LabelComposer — Admin-Werkzeug für gemischte Etikett-Bögen.
 *
 * Der Admin wählt aus der aktuellen Produktliste (nur aktive, keine Drafts,
 * keine deaktivierten). Pro Auswahl legt er Menge + Größe fest, das PDF wird
 * live vom Backend erzeugt (Namen/Beschreibungen kommen frisch aus der DB).
 *
 * Größen:
 *   small  → 40×30 mm (45 pro A4-Seite)
 *   medium → 60×40 mm (21 pro A4-Seite)
 *   large  → 90×55 mm ( 8 pro A4-Seite)
 */

const SIZE_INFO = {
  small: { label: 'Klein · 40×30 mm', perPage: 45 },
  medium: { label: 'Mittel · 60×40 mm', perPage: 21 },
  large: { label: 'Groß · 90×55 mm', perPage: 8 },
  businesscard: { label: 'Visitenkarte · 85×55 mm', perPage: 10 },
  strip: { label: 'Paket-Streifen · 150×30 mm', perPage: 9 },
  flyer_a4: { label: 'Flyer A4 · Vollformat', perPage: 1 },
};

const TEMPLATES = [
  {
    key: 'thanks',
    icon: '🤍',
    title: 'Danke-Karte',
    subtitle: 'Vielen Dank für deinen Einkauf',
    defaultSize: 'medium',
    sizes: ['small', 'medium', 'large'],
    optionsFields: [],
  },
  {
    key: 'businesscard',
    icon: '💳',
    title: 'Visitenkarte',
    subtitle: 'Logo + QR + Instagram',
    defaultSize: 'businesscard',
    sizes: ['businesscard', 'large'],
    optionsFields: [
      { key: 'website', label: 'Website', default: 'homeyoursoul.com' },
      { key: 'instagram', label: 'Instagram-Handle', default: '@pys_homeyoursoul' },
      { key: 'email', label: 'Kontakt-E-Mail', default: 'kontakt@homeyoursoul.shop' },
      { key: 'tagline', label: 'Kurzbeschreibung', default: 'Lieblingsstücke für dich und dein Zuhause' },
    ],
  },
  {
    key: 'discount',
    icon: '🎟️',
    title: 'Rabatt-Karte',
    subtitle: 'Prozent + einmaliger Code',
    defaultSize: 'medium',
    sizes: ['small', 'medium', 'large', 'businesscard'],
    optionsFields: [
      { key: 'percent', label: 'Prozent (%)', default: '10', type: 'number' },
      { key: 'code', label: 'Rabatt-Code', default: 'DANKE10' },
      { key: 'validity', label: 'Gültigkeitshinweis', default: 'Nur einmal einlösbar' },
    ],
  },
  {
    key: 'shopname',
    icon: '📦',
    title: 'Paket-Klebestreifen',
    subtitle: 'Logo + Shop-Name · 15 cm breit',
    defaultSize: 'strip',
    sizes: ['strip'],
    optionsFields: [],
  },
  {
    key: 'instagram',
    icon: '📸',
    title: 'Instagram-Karte',
    subtitle: 'Folge uns · QR direkt zum IG-Profil',
    defaultSize: 'businesscard',
    sizes: ['businesscard', 'medium', 'large'],
    optionsFields: [
      { key: 'instagram', label: 'Instagram-Handle', default: '@pys_homeyoursoul' },
      { key: 'cta', label: 'Aufruf-Text', default: 'Folgt uns für mehr auf Instagram' },
    ],
  },
  {
    key: 'flyer_a4',
    icon: '📄',
    title: 'Flyer A4',
    subtitle: 'DIN A4 zum Ausdrucken & Verteilen',
    defaultSize: 'flyer_a4',
    sizes: ['flyer_a4'],
    defaultQuantity: 1,
    optionsFields: [
      {
        key: 'season',
        label: 'Design-Variante',
        default: 'klassisch',
        type: 'select',
        options: [
          { value: 'klassisch', label: '✨ Klassisch (HYS-Logo-Sparkles)' },
          { value: 'auto',      label: '📅 Auto (aktuelle Jahreszeit)' },
          { value: 'frühling',  label: '🌸 Frühling (Kirschblüten)' },
          { value: 'sommer',    label: '☀️ Sommer (Sonne + Wellen)' },
          { value: 'herbst',    label: '🍂 Herbst (fallende Blätter)' },
          { value: 'winter',    label: '❄️ Winter / Weihnachten (Schneeflocken)' },
        ],
      },
      { key: 'title', label: 'Überschrift', default: 'Unsere Auswahl für dich' },
      { key: 'subtitle', label: 'Untertitel', default: 'Entdecke unseren Online-Shop — stylishe Accessoires & Homedekorationen und vieles mehr' },
      { key: 'handmade', label: 'Handgemachtes · Hauptzeile (leer = Auto-Preis)', default: '' },
      { key: 'accessoires', label: 'Accessoires · Hauptzeile (leer = Auto-Preis)', default: '' },
      { key: 'accessoires_hint', label: 'Accessoires · Beispiele', default: 'Haargummis · Spangen · Handtaschen · Ketten' },
      { key: 'home', label: 'Home-Deko · Hauptzeile (leer = Auto-Preis)', default: '' },
      { key: 'home_hint', label: 'Home-Deko · Beispiele', default: 'Federmäppchen · Notizblöcke · Stifte · Kerzen-Deko' },
      { key: 'website', label: 'Website', default: 'homeyoursoul.com' },
      { key: 'instagram', label: 'Instagram-Handle', default: '@pys_homeyoursoul' },
      { key: 'email', label: 'Kontakt-E-Mail (Fußzeile)', default: 'kontakt@homeyoursoul.shop' },
    ],
  },
];

const FLYER_SEASONS = [
  { value: 'klassisch', label: '✨ Klassisch' },
  { value: 'auto',      label: '📅 Auto' },
  { value: 'frühling',  label: '🌸 Frühling' },
  { value: 'sommer',    label: '☀️ Sommer' },
  { value: 'herbst',    label: '🍂 Herbst' },
  { value: 'winter',    label: '❄️ Winter' },
];

/**
 * Kleine Galerie aller Saison-Designs für einen Flyer-Bogen.
 * Rendert 6 anklickbare Vorschau-Bilder — Auswahl setzt options.season.
 * Die 6 Miniatur-Vorschauen werden einmalig geladen und im Modul-State
 * gecacht, damit weitere Flyer-Zeilen sie sofort wiederverwenden.
 */
const seasonThumbCache = { data: null, loading: null };

const SeasonGallery = ({ selected, onSelect, rowOptions }) => {
  const [thumbs, setThumbs] = React.useState(seasonThumbCache.data);
  const [loading, setLoading] = React.useState(!seasonThumbCache.data);

  React.useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      if (seasonThumbCache.data) {
        setThumbs(seasonThumbCache.data);
        setLoading(false);
        return;
      }
      if (seasonThumbCache.loading) {
        try {
          const cached = await seasonThumbCache.loading;
          if (!cancelled) { setThumbs(cached); setLoading(false); }
        } catch { if (!cancelled) setLoading(false); }
        return;
      }
      seasonThumbCache.loading = (async () => {
        const results = {};
        // Nacheinander, damit das Backend nicht 6 parallele PDF-Rendere macht.
        for (const s of FLYER_SEASONS) {
          try {
            const res = await axiosInstance.post(
              '/products/labels/compose-preview',
              { items: [{ template: 'flyer_a4', size: 'flyer_a4', quantity: 1,
                          options: { ...(rowOptions || {}), season: s.value } }] },
              { responseType: 'blob' },
            );
            results[s.value] = URL.createObjectURL(res.data);
          } catch (e) {
            console.warn(`Season-Thumb '${s.value}' fehlgeschlagen`, e);
          }
        }
        seasonThumbCache.data = results;
        return results;
      })();
      try {
        const data = await seasonThumbCache.loading;
        if (!cancelled) { setThumbs(data); setLoading(false); }
      } catch { if (!cancelled) setLoading(false); }
    }
    loadAll();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="pl-7 mt-2" data-testid="season-gallery">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-widest text-[#8B7355] font-semibold">
          Design vergleichen · klicke zum Übernehmen
        </span>
        {loading && (
          <span className="text-[10px] text-[#8B7355] italic">lädt …</span>
        )}
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {FLYER_SEASONS.map((s) => {
          const src = thumbs?.[s.value];
          const isActive = selected === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => onSelect(s.value)}
              className={`group relative border-2 transition-all bg-white overflow-hidden ${
                isActive
                  ? 'border-[#5D4E3C] shadow-md'
                  : 'border-[#F0E4D0] hover:border-[#C4A084]'
              }`}
              title={s.label}
              data-testid={`season-thumb-${s.value}`}
            >
              <div className="aspect-[210/297] bg-[#FAF5EF] flex items-center justify-center">
                {src ? (
                  <img
                    src={src}
                    alt={s.label}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <span className="text-[10px] text-[#8B7355] italic">…</span>
                )}
              </div>
              <div className={`text-center text-[10px] py-1 px-1 font-semibold truncate ${
                isActive ? 'bg-[#5D4E3C] text-[#F5EDE4]' : 'bg-white text-[#5D4E3C]'
              }`}>
                {s.label}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const LabelComposer = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]); // [{ product_id, quantity, size }]
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [inventurCategory, setInventurCategory] = useState('__all__');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(
          '/products?active_only=true&include_vendor_info=false'
        );
        if (!cancelled) setProducts(res.data || []);
      } catch (e) {
        toast.error('Produkte konnten nicht geladen werden');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredProducts = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.toLowerCase();
    return products.filter(
      (p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.sku || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q)
    );
  }, [products, search]);

  const rowIndexByProductId = useMemo(() => {
    const map = {};
    rows.forEach((r, i) => {
      map[r.product_id] = i;
    });
    return map;
  }, [rows]);

  const addRow = (product) => {
    if (rowIndexByProductId[product.id] !== undefined) {
      toast.info(`„${product.name}" ist bereits in der Liste`);
      return;
    }
    setRows((prev) => [
      ...prev,
      { template: 'product', product_id: product.id, name: product.name, quantity: 1, size: 'small' },
    ]);
  };

  const addTemplateRow = (tpl) => {
    const options = {};
    (tpl.optionsFields || []).forEach((f) => { options[f.key] = f.default; });
    setRows((prev) => [
      ...prev,
      {
        template: tpl.key,
        product_id: null,
        name: tpl.title,
        quantity: tpl.defaultQuantity ?? 5,
        size: tpl.defaultSize,
        options,
      },
    ]);
    toast.success(`„${tpl.title}" hinzugefügt`);
  };

  const addBeilegerPaket = () => {
    // „Beileger-Paket": Flyer A4 (1×) + Rabatt-Karte (10×) + Visitenkarte (10×)
    // in einem Rutsch ergänzen — perfekt zum Beilegen in jede Bestellung.
    const wanted = ['flyer_a4', 'discount', 'businesscard'];
    const newRows = wanted
      .map((key) => TEMPLATES.find((t) => t.key === key))
      .filter(Boolean)
      .map((tpl) => {
        const options = {};
        (tpl.optionsFields || []).forEach((f) => { options[f.key] = f.default; });
        return {
          template: tpl.key,
          product_id: null,
          name: tpl.title,
          quantity: tpl.defaultQuantity ?? (tpl.key === 'flyer_a4' ? 1 : 10),
          size: tpl.defaultSize,
          options,
        };
      });
    setRows((prev) => [...prev, ...newRows]);
    toast.success('🎁 Beileger-Paket hinzugefügt — Flyer + Rabatt-Karte + Visitenkarte');
  };

  const addAllProducts = () => {
    // Inventur-Button: alle aktiven Artikel als kleine Produkt-Etiketten
    // in einem Rutsch hinzufügen (Menge 1, Größe „klein"). Bereits enthaltene
    // Produkte werden übersprungen, damit ein zweiter Klick keine Duplikate erzeugt.
    // Optional per Kategorie eingeschränkt (Dropdown daneben, __all__ = alle).
    if (products.length === 0) {
      toast.info('Keine aktiven Artikel zum Hinzufügen');
      return;
    }
    const pool = inventurCategory === '__all__'
      ? products
      : products.filter((p) => (p.category || 'ohne Kategorie') === inventurCategory);
    if (pool.length === 0) {
      toast.info(`Keine Artikel in der Kategorie „${inventurCategory}"`);
      return;
    }
    const existingIds = new Set(rows.filter((r) => r.template === 'product').map((r) => r.product_id));
    const newRows = pool
      .filter((p) => !existingIds.has(p.id))
      .map((p) => ({
        template: 'product',
        product_id: p.id,
        name: p.name,
        quantity: 1,
        size: 'medium',  // Lager-Etiketten: „Mittel" 60×40 mm — genug Platz für Produktbild
        options: { show_image: true },  // Standard: mit Produktbild
      }));
    if (newRows.length === 0) {
      toast.info('Alle passenden Artikel sind bereits in der Liste');
      return;
    }
    const scope = inventurCategory === '__all__' ? 'alle Kategorien' : `Kategorie „${inventurCategory}"`;
    setRows((prev) => [...prev, ...newRows]);
    toast.success(`🏷️ ${newRows.length} Artikel hinzugefügt (${scope} · Mittel 60×40 mm · mit Bild)`);
  };

  // Sortierte, deduplizierte Kategorienliste für das Inventur-Dropdown
  const inventurCategoryOptions = useMemo(() => {
    const set = new Set();
    products.forEach((p) => set.add(p.category || 'ohne Kategorie'));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'de'));
  }, [products]);

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const totals = useMemo(() => {
    const bySize = { small: 0, medium: 0, large: 0, businesscard: 0, strip: 0, flyer_a4: 0 };
    rows.forEach((r) => {
      bySize[r.size] = (bySize[r.size] || 0) + Number(r.quantity || 0);
    });
    const pages = Object.entries(bySize).reduce((sum, [key, count]) => {
      const info = SIZE_INFO[key];
      return sum + (count > 0 && info ? Math.ceil(count / info.perPage) : 0);
    }, 0);
    const totalLabels = Object.values(bySize).reduce((a, b) => a + b, 0);
    return { bySize, pages, totalLabels };
  }, [rows]);

  const generatePdf = async () => {
    if (rows.length === 0) {
      toast.error('Bitte mindestens ein Element hinzufügen');
      return;
    }
    setGenerating(true);
    try {
      const res = await axiosInstance.post(
        '/products/labels/compose-pdf',
        {
          items: rows.map((r) => ({
            template: r.template === 'product' ? null : r.template,
            product_id: r.template === 'product' ? r.product_id : null,
            quantity: Number(r.quantity) || 1,
            size: r.size,
            options: r.options || {},
          })),
        },
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success(`PDF erzeugt — ${totals.pages} A4-Seite(n)`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`PDF-Erzeugung fehlgeschlagen: ${detail}`);
    } finally {
      setGenerating(false);
    }
  };

  const previewImage = async () => {
    if (rows.length === 0) {
      toast.error('Bitte mindestens ein Element hinzufügen');
      return;
    }
    setPreviewing(true);
    try {
      const res = await axiosInstance.post(
        '/products/labels/compose-preview',
        {
          items: rows.map((r) => ({
            template: r.template === 'product' ? null : r.template,
            product_id: r.template === 'product' ? r.product_id : null,
            quantity: Number(r.quantity) || 1,
            size: r.size,
            options: r.options || {},
          })),
        },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'image/png' }));
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Vorschau fehlgeschlagen: ${detail}`);
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6" data-testid="label-composer-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-[#8B7355] hover:text-[#5D4E3C] text-sm mb-3"
      >
        <ArrowLeft className="h-4 w-4" /> Zurück
      </button>

      <h1 className="text-2xl md:text-3xl font-bold text-[#5D4E3C] mb-1" style={{ fontFamily: 'Georgia, serif' }}>
        Etiketten drucken
      </h1>
      <p className="text-sm text-[#8B7355] mb-5">
        Wähle die Artikel, Menge und Größe. Es werden nur aktive Artikel gedruckt. Namen und
        Beschreibungen werden bei jeder Erzeugung frisch aus der Produktliste geladen.
      </p>

      <div className="grid gap-4 lg:grid-cols-[1fr,minmax(0,420px)]">
        {/* --- Left: product picker + templates --- */}
        <div className="space-y-4">
          {/* Vorlagen-Kacheln */}
          <div className="border border-[#E8DCC8] bg-white p-3 md:p-4">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h2 className="text-sm font-bold text-[#5D4E3C] uppercase tracking-wider">
                Vorlagen (Danke, Visitenkarte, Rabatt, Paket-Streifen)
              </h2>
              <button
                type="button"
                onClick={addBeilegerPaket}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FBE4DA] to-[#F5EDE4] hover:from-[#F5DAC8] hover:to-[#EFE5D7] border border-[#C4A084] text-[#5D4E3C] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors"
                title="Flyer + Rabatt-Karte + Visitenkarte in einem Rutsch"
                data-testid="label-beileger-paket-btn"
              >
                🎁 Beileger-Paket
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {TEMPLATES.map((tpl) => (
                <button
                  key={tpl.key}
                  type="button"
                  onClick={() => addTemplateRow(tpl)}
                  className="border border-[#E8DCC8] hover:border-[#C4A084] hover:bg-[#FAF5EE] p-3 text-left transition-colors flex flex-col min-h-[110px]"
                  data-testid={`label-template-${tpl.key}`}
                >
                  <div className="text-2xl mb-1">{tpl.icon}</div>
                  <div className="text-sm font-semibold text-[#5D4E3C]">{tpl.title}</div>
                  <div className="text-[11px] text-[#8B7355] mt-0.5">{tpl.subtitle}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Produkt-Picker */}
          <div className="border border-[#E8DCC8] bg-white p-3 md:p-4">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h2 className="text-sm font-bold text-[#5D4E3C] uppercase tracking-wider">
                Produkt-Etiketten
              </h2>
              <div className="flex items-center gap-1.5 flex-wrap">
                <select
                  value={inventurCategory}
                  onChange={(e) => setInventurCategory(e.target.value)}
                  disabled={loading || inventurCategoryOptions.length === 0}
                  className="border border-[#C4A084] bg-white text-[#5D4E3C] text-xs px-2 py-1.5 focus:border-[#8B7355] outline-none disabled:opacity-50"
                  title="Kategorie einschränken für den Inventur-Button"
                  data-testid="label-inventur-category-select"
                >
                  <option value="__all__">🎨 Alle Kategorien</option>
                  {inventurCategoryOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={addAllProducts}
                  disabled={loading || products.length === 0}
                  className="inline-flex items-center gap-2 bg-[#F5EDE4] hover:bg-[#EFE5D7] disabled:opacity-50 disabled:cursor-not-allowed border border-[#C4A084] text-[#5D4E3C] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors"
                  title={
                    inventurCategory === '__all__'
                      ? `Fügt alle ${products.length} aktiven Artikel als kleine Etiketten (Menge 1) hinzu`
                      : `Fügt alle Artikel der Kategorie „${inventurCategory}" als kleine Etiketten hinzu`
                  }
                  data-testid="label-add-all-products-btn"
                >
                  🏷️ Inventur ({inventurCategory === '__all__'
                    ? products.length
                    : products.filter((p) => (p.category || 'ohne Kategorie') === inventurCategory).length})
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <Search className="h-4 w-4 text-[#8B7355]" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Artikel suchen (Name, SKU, Kategorie)…"
                className="flex-1 border border-[#E8DCC8] px-3 py-2 text-sm focus:border-[#C4A084] outline-none"
                data-testid="label-search-input"
              />
            </div>

            {loading ? (
              <p className="text-sm text-[#8B7355] py-8 text-center">Lade Produkte…</p>
            ) : filteredProducts.length === 0 ? (
              <p className="text-sm text-[#8B7355] py-8 text-center">Keine Treffer.</p>
            ) : (
              <ul className="divide-y divide-[#F0E4D0] max-h-[420px] overflow-auto">
                {filteredProducts.slice(0, 200).map((p) => {
                  const inList = rowIndexByProductId[p.id] !== undefined;
                  return (
                    <li key={p.id} className="flex items-center gap-3 py-2">
                      {p.images?.[0] ? (
                        <img
                          src={p.images[0]}
                          alt=""
                          className="w-12 h-12 object-cover border border-[#F0E4D0]"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-[#FAF5EE] border border-[#F0E4D0]" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#5D4E3C] truncate">{p.name}</div>
                        <div className="text-[11px] text-[#8B7355]">
                          SKU {p.sku || '—'} · {p.category || 'ohne Kategorie'}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => addRow(p)}
                        disabled={inList}
                        className="text-xs bg-[#5D4E3C] hover:bg-[#4A3E30] disabled:bg-[#C4A084] disabled:cursor-not-allowed text-white px-3 py-1.5 inline-flex items-center gap-1"
                        data-testid={`label-add-${p.id}`}
                      >
                        {inList ? '✓ Hinzugefügt' : <><Plus className="h-3.5 w-3.5" /> Hinzufügen</>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* --- Right: Composer + Actions --- */}
        <div className="border border-[#E8DCC8] bg-white p-3 md:p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-sm font-bold text-[#5D4E3C] uppercase tracking-wider">
              Etiketten-Bogen ({totals.totalLabels})
            </h2>
            {rows.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Alle ${rows.length} Einträge aus der Druckübersicht entfernen?`)) {
                    setRows([]);
                    toast.success('Druckübersicht geleert');
                  }
                }}
                className="inline-flex items-center gap-1 bg-[#FDECEA] hover:bg-[#F8CFC5] text-[#B0512E] border border-[#E8B4A5] px-2 py-1.5 text-xs font-semibold uppercase tracking-wider"
                title="Alle Zeilen aus der Druckübersicht entfernen"
                data-testid="label-clear-all-btn"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Alle entfernen
              </button>
            )}
          </div>
          {rows.length === 0 ? (
            <p className="text-sm text-[#8B7355] py-6 text-center">
              Noch nichts gewählt. Vorlagen oben oder Produkte links „Hinzufügen&quot; klicken.
            </p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-auto pr-1">
              {rows.map((r, idx) => {
                const tpl = TEMPLATES.find((t) => t.key === r.template);
                const availableSizes = tpl ? tpl.sizes : ['small', 'medium', 'large'];
                return (
                  <div
                    key={`${r.template}-${r.product_id || idx}`}
                    className="border border-[#F0E4D0] p-2 space-y-1.5"
                    data-testid={`label-row-${r.product_id || r.template}-${idx}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg" title={tpl?.title || 'Produkt'}>
                        {tpl?.icon || '🏷️'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#5D4E3C] truncate">{r.name}</div>
                      </div>
                      <input
                        type="number"
                        min="1"
                        max="500"
                        value={r.quantity}
                        onChange={(e) => updateRow(idx, { quantity: Math.max(1, parseInt(e.target.value || '1', 10)) })}
                        className="w-14 border border-[#E8DCC8] px-2 py-1 text-sm text-center focus:border-[#C4A084] outline-none"
                        aria-label="Menge"
                      />
                      <select
                        value={r.size}
                        onChange={(e) => updateRow(idx, { size: e.target.value })}
                        className="border border-[#E8DCC8] px-2 py-1 text-xs bg-white focus:border-[#C4A084] outline-none"
                        aria-label="Größe"
                      >
                        {availableSizes.map((s) => (
                          <option key={s} value={s}>{SIZE_INFO[s]?.label || s}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        className="shrink-0 inline-flex items-center gap-1 bg-[#FDECEA] hover:bg-[#F8CFC5] active:bg-[#F0B6A9] text-[#B0512E] border border-[#E8B4A5] px-2 py-1.5 text-xs font-semibold transition-colors min-w-[44px] min-h-[36px] justify-center"
                        title="Diese Zeile aus der Druckübersicht entfernen"
                        aria-label="Entfernen"
                        data-testid={`label-row-remove-${idx}`}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="hidden sm:inline">Entfernen</span>
                      </button>
                    </div>

                    {/* Produkt-Etiketten: Toggle „mit Bild drucken" (default: Bild an, wirkt ab Größe „Mittel") */}
                    {r.template === 'product' && (
                      <label className="pl-7 flex items-center gap-2 text-xs text-[#5D4E3C] cursor-pointer"
                             data-testid={`label-showimage-toggle-${idx}`}>
                        <input
                          type="checkbox"
                          checked={(r.options?.show_image !== false)}
                          onChange={(e) => updateRow(idx, {
                            options: { ...(r.options || {}), show_image: e.target.checked },
                          })}
                          className="accent-[#5D4E3C]"
                        />
                        <span>
                          🖼️ Produktbild aufdrucken
                          {r.size === 'small' && (
                            <span className="text-[#B0512E]"> {'(nur ab Größe „Mittel" 60×40 mm sichtbar)'}</span>
                          )}
                        </span>
                      </label>
                    )}

                    {/* Options-Inputs für Templates mit Optionen */}
                    {tpl && tpl.optionsFields && tpl.optionsFields.length > 0 && (
                      <div className="pl-7 grid grid-cols-1 gap-1.5">
                        {tpl.optionsFields.map((f) => (
                          f.type === 'select' ? (
                            <select
                              key={f.key}
                              value={(r.options || {})[f.key] ?? f.default}
                              onChange={(e) => updateRow(idx, {
                                options: { ...(r.options || {}), [f.key]: e.target.value },
                              })}
                              className="w-full border border-[#F0E4D0] bg-white px-2 py-1 text-xs focus:border-[#C4A084] outline-none"
                              data-testid={`label-option-${tpl.key}-${f.key}-${idx}`}
                              title={f.label}
                            >
                              {(f.options || []).map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              key={f.key}
                              type={f.type || 'text'}
                              value={(r.options || {})[f.key] ?? f.default}
                              onChange={(e) => updateRow(idx, {
                                options: { ...(r.options || {}), [f.key]: e.target.value },
                              })}
                              placeholder={f.label}
                              className="w-full border border-[#F0E4D0] px-2 py-1 text-xs focus:border-[#C4A084] outline-none"
                              data-testid={`label-option-${tpl.key}-${f.key}-${idx}`}
                            />
                          )
                        ))}
                      </div>
                    )}
                    {/* Saison-Galerie: nur beim Flyer A4 zeigen */}
                    {tpl && tpl.key === 'flyer_a4' && (
                      <SeasonGallery
                        selected={(r.options || {}).season ?? 'klassisch'}
                        rowOptions={r.options}
                        onSelect={(value) => updateRow(idx, {
                          options: { ...(r.options || {}), season: value },
                        })}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Summary + Generate button */}
          <div className="mt-4 border-t border-[#F0E4D0] pt-3 space-y-1.5 text-xs text-[#5D4E3C]">
            {Object.entries(SIZE_INFO).map(([key, info]) => (
              totals.bySize[key] > 0 ? (
                <div key={key} className="flex justify-between">
                  <span>{info.label}</span>
                  <span><strong>{totals.bySize[key]}</strong> Stück</span>
                </div>
              ) : null
            ))}
            {totals.totalLabels === 0 && (
              <div className="text-[#8B7355] italic">Noch nichts hinzugefügt</div>
            )}
            <div className="flex justify-between pt-2 border-t border-[#F0E4D0] font-semibold">
              <span>A4-Seiten insgesamt</span>
              <span>{totals.pages}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={generatePdf}
            disabled={generating || rows.length === 0}
            className="mt-4 bg-[#5D4E3C] hover:bg-[#4A3E30] disabled:bg-[#C4A084] disabled:cursor-not-allowed text-white px-4 py-2.5 inline-flex items-center justify-center gap-2 uppercase text-sm tracking-wider"
            data-testid="label-generate-btn"
          >
            <Printer className="h-4 w-4" />
            {generating ? 'Wird erzeugt…' : 'PDF erzeugen'}
          </button>

          <button
            type="button"
            onClick={previewImage}
            disabled={previewing || rows.length === 0}
            className="mt-4 ml-2 bg-[#F5EDE4] hover:bg-[#EFE5D7] disabled:opacity-50 text-[#5D4E3C] border border-[#C4A084] px-4 py-2.5 inline-flex items-center justify-center gap-2 uppercase text-sm tracking-wider"
            data-testid="label-preview-btn"
          >
            {previewing ? 'Rendert…' : '👁 Vorschau als Bild'}
          </button>

          {previewUrl && (
            <div className="mt-6 p-3 md:p-4 bg-white border border-[#C4A084]">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-widest text-[#8B7355]">
                  Live-Vorschau · {totals.pages} Seite{totals.pages === 1 ? '' : 'n'}
                </span>
                <button
                  type="button"
                  onClick={() => { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
                  className="text-xs text-[#8B7355] hover:text-[#5D4E3C] underline"
                >
                  Schließen
                </button>
              </div>
              {/* Mobile: horizontal scrollbar Container, damit lange/mehrseitige Vorschau
                  nicht abgeschnitten wird. Bild wird proportional angezeigt und darf über
                  die Viewport-Breite hinausgehen. */}
              <div className="w-full overflow-auto max-h-[75vh] border border-[#E8DCC8] bg-[#FAF5EF]">
                <img
                  src={previewUrl}
                  alt="Vorschau"
                  className="block max-w-none md:max-w-2xl md:mx-auto"
                  style={{ minWidth: '100%' }}
                  data-testid="label-preview-image"
                />
              </div>
              <p className="mt-2 text-[10px] text-[#8B7355] italic md:hidden">
                Tipp: Vorschau in beide Richtungen scrollbar
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LabelComposer;
